// contexts/AlertContext.tsx
// Global alert state with real-time subscription to critical moderation flags

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
    subscribeToCriticalFlags,
    fetchRecentAlerts,
    acknowledgeAlert as acknowledgeAlertService,
    sendAlert,
    Alert,
    AlertChannel,
} from '../services/omoreAlertingService';
import { useAuth } from './AuthContext';

interface AlertContextType {
    alerts: Alert[];
    unacknowledgedCount: number;
    acknowledgeAlertById: (alertId: string) => Promise<void>;
    acknowledgeAll: () => Promise<void>;
    isAlertPanelOpen: boolean;
    setAlertPanelOpen: (open: boolean) => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

export function AlertProvider({ children }: { children: React.ReactNode }) {
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [isAlertPanelOpen, setAlertPanelOpen] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!currentUser?.email) return;

        // Load existing unacknowledged alerts on mount
        fetchRecentAlerts(50, true).then(setAlerts).catch(console.error);

        // Subscribe to new critical flags in real-time
        const unsubscribe = subscribeToCriticalFlags(
            {
                criticalCategories: ['self_harm', 'violence', 'sexual'],
                minSeverityThreshold: 4,
                channels: [AlertChannel.IN_APP, AlertChannel.EMAIL],
                recipients: [currentUser.email],
                maxAlertsPerHour: 10,
            },
            async (newAlert) => {
                setAlerts(prev => [newAlert, ...prev]);

                // Dispatch to external channels
                try {
                    await sendAlert(newAlert);
                } catch (err) {
                    console.error('Error dispatching alert:', err);
                }

                // Show browser notification if permitted
                if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                    new Notification(`Omore ${newAlert.severity.toUpperCase()} Alert`, {
                        body: `${newAlert.flagCategories.join(', ')} detected from ${newAlert.explorerName}`,
                        requireInteraction: true,
                    });
                }
            }
        );

        // Request browser notification permission
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        return () => unsubscribe();
    }, [currentUser?.email]);

    const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;

    const acknowledgeAlertById = useCallback(async (alertId: string) => {
        if (!currentUser?.email) return;
        await acknowledgeAlertService(alertId, currentUser.email);
        setAlerts(prev =>
            prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a)
        );
    }, [currentUser?.email]);

    const acknowledgeAll = useCallback(async () => {
        if (!currentUser?.email) return;
        const unacknowledged = alerts.filter(a => !a.acknowledged);
        for (const alert of unacknowledged) {
            try {
                await acknowledgeAlertService(alert.id, currentUser.email);
            } catch (err) {
                console.error('Error acknowledging alert:', alert.id, err);
            }
        }
        setAlerts(prev => prev.map(a => ({ ...a, acknowledged: true })));
    }, [alerts, currentUser?.email]);

    return (
        <AlertContext.Provider value={{
            alerts,
            unacknowledgedCount,
            acknowledgeAlertById,
            acknowledgeAll,
            isAlertPanelOpen,
            setAlertPanelOpen,
        }}>
            {children}
        </AlertContext.Provider>
    );
}

export const useAlerts = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlerts must be used within an AlertProvider');
    }
    return context;
};
